import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { Card, Empty } from "antd";
import { ExportToExcel } from "../../../utils";


export const PieCourier = ({ data }) => {
  const [chartData, setChartData] = useState([]);
  const [detailData,setDetailData] = useState([]);

  useEffect(() => {
    const processedData = processData(data);
    const detailedData = processExcelData(data); 
    setChartData(processedData);
    setDetailData(detailedData);
  }, [data]);

  const processData = (data) => {
    let salesByCourier = {};
    data?.forEach((order) => {
      if (order.status === "Entregado") {
        if (!salesByCourier[order.courier]) {
          salesByCourier[order.courier] = {
            totalSales: 0,
            totalItems: 0,
            totalOrders: 0,
          };
        }
        salesByCourier[order.courier].totalSales += order.saleAmount;
        order.products?.forEach((item) => {
          salesByCourier[order.courier].totalItems += item.quantity;
        });
        salesByCourier[order.courier].totalOrders++;
      }
    });

    const chartData = salesByCourier
      ? Object.keys(salesByCourier).map((courier) => {
        const courierData = salesByCourier[courier];
        return {
          courier,
          totalSales: courierData.totalSales,
          totalItems: courierData.totalItems,
          totalOrders: courierData.totalOrders,
          averageTicket: courierData.totalItems / courierData.totalOrders,
        };
      })
      : [];

    return chartData;
  };

  const processExcelData = (data) => {
    let salesByCourier = {};
    data?.forEach((order) => {
      if (order.status === "Entregado") {
        if (!salesByCourier[order.courier]) {
          salesByCourier[order.courier] = {};
        }
        if(!salesByCourier[order.courier][order.district]){
          salesByCourier[order.courier][order.district] = {
            totalCustomers:0,
            totalSales: 0,
            totalItems: 0,
            totalOrders: 0,
          };
        }
        if(!salesByCourier[order.courier][order.district][order.customerName]){
          salesByCourier[order.courier][order.district][order.customerName] = {};
          salesByCourier[order.courier][order.district].totalCustomers++;
        }
        salesByCourier[order.courier][order.district].totalSales += order.saleAmount;
        order.products?.forEach((item) => {
          salesByCourier[order.courier][order.district].totalItems += item.quantity;
        });
        salesByCourier[order.courier][order.district].totalOrders++;
      }
    });

    const exportData = salesByCourier
      ? Object.keys(salesByCourier).map((courier) => {
          return Object.keys(salesByCourier[courier]).map((district)=>{
            const courierData = salesByCourier[courier][district];
          return {
            courier,
            district,
            totalCustomers:courierData.totalCustomers,
            totalSales: courierData.totalSales,
            totalItems: courierData.totalItems,
            totalOrders: courierData.totalOrders,
            averageTicket: courierData.totalItems / courierData.totalOrders,
          };
        });
      })
      : [];

    return exportData;
  };

  const COLORS = [
    "#244861",
    "#275F6F",
    "#2B767E",
    "#2F8E8D",
    "#33A79C",
    "#37BFAB",
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
          }}
        >
          <b className="intro">{`${payload[0].name}`}</b>
          <p className="intro">{`Ventas totales: S/ ${payload[0].payload.totalSales.toFixed(
            2
          )}`}</p>
          <p className="desc">{`Cantidad de productos: ${payload[0].payload.totalItems}`}</p>
          <p className="desc">{`Cantidad de ordenes: ${payload[0].payload.totalOrders}`}</p>
          <p className="desc">{`Ticket promedio: ${payload[0].payload.averageTicket.toFixed(
            2
          )}`}</p>
        </div>
      );
    }

    return null;
  };

  const totalSales = chartData.reduce(
    (total, courier) => total + courier.totalSales,
    0
  );

  const excelData = chartData.map((courier) => ({
    Courier: courier.courier,
    "Ventas totales": `S/ ${courier.totalSales.toFixed(2)}`,
    "Total de Productos": courier.totalItems,
    "Total de Órdenes": courier.totalOrders,
    "Ticket Promedio": courier.averageTicket.toFixed(2),
    "Porcentaje de Ventas": `${(
      (courier.totalSales / totalSales) *
      100
    ).toFixed(2)}%`,
  }));

  const flatData = [];

  // eslint-disable-next-line
  detailData.map((row)=>{
    // eslint-disable-next-line
    row.map((record)=>{
      flatData.push({
        Courier:record.courier,
        District:record.district,
        TotalCustomers:record.totalCustomers,
        "Ventas totales": `S/ ${record.totalSales.toFixed(2)}`,
        "Total de Productos": record.totalItems,
        "Total de Órdenes": record.totalOrders,
        "Ticket Promedio": record.averageTicket.toFixed(2),
        "Porcentaje de Ventas": `${(
          (record.totalSales / totalSales) *
          100
        ).toFixed(2)}%`,
      });
    });
  });

  const excelColumns = [
    {
      title: "Courier",
      dataIndex: "Courier",
      key: "Courier",
      width: 150,
    },
    {
      title: "Ventas totales",
      dataIndex: "Ventas totales",
      key: "Ventas totales",
    },
    {
      title: "Total de Productos",
      dataIndex: "Total de Productos",
      key: "Total de Productos",
    },
    {
      title: "Total de Órdenes",
      dataIndex: "Total de Órdenes",
      key: "Total de Órdenes",
    },
    {
      title: "Ticket Promedio",
      dataIndex: "Ticket Promedio",
      key: "Ticket Promedio",
    },
    {
      title: "Porcentaje de Ventas",
      dataIndex: "Porcentaje de Ventas",
      key: "Porcentaje de Ventas",
    },
  ];

  const excelDetailColumns = [
    {
      title: "Courier",
      dataIndex: "Courier",
      key: "Courier",
    },
    {
      title: "Distrito",
      dataIndex: "District",
      key: "District",
    },
    {
      title: "# Clientes",
      dataIndex: "TotalCustomers",
      key: "TotalCustomers",
    },
    {
      title: "Ventas totales",
      dataIndex: "Ventas totales",
      key: "Ventas totales",
    },
    {
      title: "Total de Productos",
      dataIndex: "Total de Productos",
      key: "Total de Productos",
    },
    {
      title: "Total de Órdenes",
      dataIndex: "Total de Órdenes",
      key: "Total de Órdenes",
    },
    {
      title: "Ticket Promedio",
      dataIndex: "Ticket Promedio",
      key: "Ticket Promedio",
    },
    {
      title: "Porcentaje de Ventas",
      dataIndex: "Porcentaje de Ventas",
      key: "Porcentaje de Ventas",
    },
  ];

  const renderCustomizedLabel = ({ name, percent }) => {
    return `${(percent * 100).toFixed(0)}%`;
  };

  return chartData.length > 0 ? (
    <Card title=" ">
      <ResponsiveContainer width="100%" height={343}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="totalSales"
            nameKey="courier"
            cx="50%"
            cy="50%"
            fill="#8884d8"
            label={renderCustomizedLabel}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend />
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={excelData}
          columns={excelColumns}
          fileName="resumenCourier"
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={flatData}
          columns={excelDetailColumns}
          fileName="detailCourier"
          name="detalle distrito"
        />
      </div>
    </Card>
  ) : (
    <Card title=" ">
      <div
        style={{
          height: 385,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Empty description="No hay datos disponibles" />
      </div>
    </Card>
  );
};
